import type { TranscriptChunk } from '@/shared/api/transcriptor';

// Provider events reduced to what the transcript needs. `itemId` identifies
// one committed phrase on the provider side. A `partial` appends to the
// phrase's text in progress; an `interim` replaces it, and an empty one
// removes it.
export type LiveTranscriptionEvent =
  | { type: 'partial'; itemId: string; text: string }
  | { type: 'interim'; itemId: string; text: string }
  | { type: 'committed'; itemId: string }
  | { type: 'completed'; itemId: string; text: string }
  | { type: 'failed'; itemId: string };

type LiveSegment = { itemId: string | null; start: number; end: number };

export type LiveTranscriptState = {
  // Phrases committed by the client, oldest first, not yet emitted.
  segments: LiveSegment[];
  partials: Record<string, string>;
  // Final text by provider item; empty when transcription failed.
  finals: Record<string, string>;
};

export const EMPTY_LIVE_TRANSCRIPT: LiveTranscriptState = {
  segments: [],
  partials: {},
  finals: {},
};

const omitKey = <T>(record: Record<string, T>, key: string) =>
  Object.fromEntries(Object.entries(record).filter(([item]) => item !== key));

// The phrase times come from the recording clock: the provider returns no
// timestamps.
export const commitLiveSegment = (
  state: LiveTranscriptState,
  { start, end }: { start: number; end: number },
): LiveTranscriptState => ({
  ...state,
  segments: [...state.segments, { itemId: null, start, end }],
});

export const applyLiveTranscriptionEvent = (
  state: LiveTranscriptState,
  event: LiveTranscriptionEvent,
): LiveTranscriptState => {
  if (event.type === 'partial') {
    return event.itemId in state.finals
      ? state
      : {
          ...state,
          partials: {
            ...state.partials,
            [event.itemId]: (state.partials[event.itemId] ?? '') + event.text,
          },
        };
  }
  if (event.type === 'interim') {
    if (event.itemId in state.finals) {
      return state;
    }
    return {
      ...state,
      partials: event.text
        ? { ...state.partials, [event.itemId]: event.text }
        : omitKey(state.partials, event.itemId),
    };
  }
  if (event.type === 'committed') {
    // Commits are acknowledged in the order they were sent.
    const index = state.segments.findIndex((segment) => !segment.itemId);
    return index < 0
      ? state
      : {
          ...state,
          segments: state.segments.map((segment, segmentIndex) =>
            segmentIndex === index
              ? { ...segment, itemId: event.itemId }
              : segment,
          ),
        };
  }
  return {
    ...state,
    partials: omitKey(state.partials, event.itemId),
    finals: {
      ...state.finals,
      [event.itemId]: event.type === 'completed' ? event.text.trim() : '',
    },
  };
};

// Completion events of different phrases may arrive out of order; chunks are
// released strictly in commit order so chunk indexes never shift.
export const takeLiveChunks = (
  state: LiveTranscriptState,
): { state: LiveTranscriptState; chunks: TranscriptChunk[] } => {
  const ready = state.segments.findIndex(
    (segment) => !segment.itemId || !(segment.itemId in state.finals),
  );
  const done = ready < 0 ? state.segments : state.segments.slice(0, ready);
  if (!done.length) {
    return { state, chunks: [] };
  }
  return {
    state: {
      ...state,
      segments: state.segments.slice(done.length),
      finals: done.reduce(
        (finals, segment) => omitKey(finals, segment.itemId ?? ''),
        state.finals,
      ),
    },
    chunks: done.flatMap((segment) => {
      const text = state.finals[segment.itemId ?? ''];
      return text ? [{ text, start: segment.start, end: segment.end }] : [];
    }),
  };
};

export const hasPendingLiveSegments = (state: LiveTranscriptState) =>
  state.segments.length > 0;

// Phrases sent over a connection that dropped will never complete.
export const abandonLiveSegments = (
  state: LiveTranscriptState,
): LiveTranscriptState => ({ ...state, segments: [], partials: {} });

export const getLivePartialText = (state: LiveTranscriptState) =>
  Object.values(state.partials)
    .map((text) => text.trim())
    .filter(Boolean)
    .join(' ');
